import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { NavigationStart, Router, Event } from '@angular/router';
import { StatefulService } from '@app/service/stateful.service';
import { LoginWidgetContextData, OIDLoginWidgetExportData, LoginWidgetStateInfo } from '@cudirect-widgets/ea-login';
import { GoogleTagManagerService, JwtToken, TokenService } from '@cudirect/shared-ui-library';
import { UserSessionService } from '@shared/service/user-session.service';
import { XApiKeyService } from '@shared/service/x-api-key.service';
import { LoginService } from '@user/service/login/login.service';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'dx-verify',
  templateUrl: './verify.component.html'
})
export class VerifyComponent implements OnInit, OnDestroy {
  @Input() userID: string;
  @Input() method: 'Email_Code' | 'Phone';
  // Defaulting this functionality to be disabled. Maybe this should be on all the time,
  // but we need to be sure. Currently, only the silent verification uses this.
  @Input() allowCloseableModal = false;
  @Output() success = new EventEmitter<OIDLoginWidgetExportData>();

  public widgetContext: LoginWidgetContextData;
  // Displays the login widget if true
  public showWidget = false;
  // Displays the error modal if true
  public showError = false;
  // Displays the retry button if true
  public showRetry = false;
  // Displays the button loader animation if true
  public loading = false;
  // Holder for subscriptions
  public subscription: Subscription;
  // This gets toggled for invalid phone number so the modal can be closed and the number changed.
  public showCloseable = false;

  constructor(
    private readonly loginService: LoginService,
    private readonly tokenService: TokenService,
    private readonly router: Router,
    private readonly userService: UserSessionService,
    private readonly xApiKeyService: XApiKeyService,
    private readonly statefulService: StatefulService,
    private readonly gtm: GoogleTagManagerService
  ) {}

  ngOnInit(): void {
    this.subscribeToPopstateEvent();
    this.loginService.getWidgetContext('verifyMFA').then((res) => {
      this.widgetContext = res;
      // TESTING ***
      // this.userID = 'MFATestUser@origence.testinator.com';
      // this.method = 'Email_Code'; // Email_Code or Phone
      // END TESTING ***
      this.widgetContext.oid.verifyUserId = this.userID;

      if (!this.tokenService.isInitialized) {
        this.tokenService
          .initialize(this.widgetContext.oid.apiBaseURL, this.xApiKeyService.createApiHeader(), this.widgetContext.oid.oidConfigName, true)
          .then(() => {
            this.sendCode();
          });
      } else {
        this.sendCode();
      }
    });
  }

  /**
   * Captures browser back button click and navigate to login page
   */
  private subscribeToPopstateEvent() {
    this.subscription = this.router.events
      .pipe(
        filter((event: Event) => {
          return event instanceof NavigationStart;
        })
      )
      .subscribe((event: NavigationStart) => {
        // - popstate (ie, browser controlled change such as Back button).
        if (event.navigationTrigger === 'popstate') {
          this.userService.logOut(true);
          this.router.navigate(['user/login']);
        }
      });
  }

  ngOnDestroy(): void {
    this.subscription && this.subscription.unsubscribe();
  }

  // Starts passwordless login. This will send an mfa code
  public sendCode(): void {
    this.showError = false;

    this.tokenService.startPasswordless(this.userID, this.method).then((res) => {
      this.loading = false;
      this.showRetry = false;
      if (res.isSuccess !== false) {
        // Code has been sent.
        this.showError = false;
        this.showWidget = true;
      } else {
        res.data = res.data as HttpErrorResponse;
        // Check if phone number was invalid
        if (res.data?.error?.message === 'StartPasswordlessInvalidPhoneNumber' && this.allowCloseableModal) {
          this.showError = true;
          this.showCloseable = true;
        } else {
          // All other errors
          this.showError = true;
          this.showRetry = true;
        }
      }
    });
  }

  public receivedDataHandler(data: OIDLoginWidgetExportData | string): void {
    data = data as OIDLoginWidgetExportData;
    this.showWidget = false;
    if (data.statusMsg === 'Authenticated') {
      this.gtm.publishEvent('arcdx.silent-verify-success', {});
      // SUCCESS!
      this.tokenService.updateToken(data.token as JwtToken);
      this.success.emit(data);
      // Continue with the success path handling here...
    } else {
      // Handle error. Should only get here if APIs are down.
      console.log('Received data handler error block');
      this.showError = true;
      this.showRetry = false;
    }
  }

  public receivedStateInfoHandler(data: LoginWidgetStateInfo | string): void {
    data = data as LoginWidgetStateInfo;
    if (data?.event === 'timeout' && data?.code === 'sendToLogin') {
      this.router.navigate(['/login']);
    }
  }

  // Handler for clicking the retry button.
  public retry(): void {
    this.loading = true;
    this.sendCode();
  }

  // Closes the error modal.
  public closeAlert(): void {
    this.statefulService.wrap('ss:showMFAVerification').value = false;
    this.showError = false;
    this.showRetry = false;
    this.loading = false;
    this.showWidget = false;
    // Navigating to the same route to trigger the save and continue button to be enabled.
    this.router.navigate([this.router.url]);
  }
}
